import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Grid, IconButton, Typography } from '@mui/material';

import { groupParentSupervisionActions } from '../../../../store';
import { FBoxContainer, FHAutocomplete, FHButton, FHTextarea } from '../../../base';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { GROUP_SN_FIELD_MIN_LENGTH, GROUP_SN_MAX_TOPICS, GROUP_SN_MIN_TOPICS } from '../constants';
import PlusIcon from '../../../svgs/PlusIcon';
import TrashIcon from '../../../svgs/TrashIcon';
import ErrorAlertIcon from '../../../svgs/ErrorAlertIcon';

import type { IOption, ITrainingTopic } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';

interface TrainingTopicItemProps {
  id: number;
  label: string;
  description: string;
  isEditable: boolean;
  validate: boolean;
  disabled: boolean;
  onDelete: (id: number) => void;
  onUpdate: (id: number, description: string) => void;
}

interface GroupTrainingTopicsProps {
  isEditable: boolean;
  validate: boolean;
}

function TrainingTopicItem({
  id,
  label,
  description,
  isEditable,
  disabled,
  onDelete,
  onUpdate,
}: TrainingTopicItemProps) {
  const [topicDescription, setTopicDescription] = useState<string>('');

  useEffect(() => {
    setTopicDescription(description);
  }, [description]);

  const showError =
    !topicDescription || topicDescription?.trim()?.length < GROUP_SN_FIELD_MIN_LENGTH;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography fontWeight={700}>{label}</Typography>
        {isEditable && (
          <IconButton onClick={() => onDelete(id)} disabled={disabled}>
            <TrashIcon />
          </IconButton>
        )}
      </Box>
      {isEditable && (
        <FHTextarea
          minRows={4}
          maxRows={12}
          sx={{ boxShadow: 'none' }}
          value={topicDescription}
          expandable={!disabled}
          disabled={disabled}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setTopicDescription(e.target.value);
          }}
          onBlur={(_e) => {
            if (
              description === topicDescription ||
              (topicDescription?.length && topicDescription?.length < GROUP_SN_FIELD_MIN_LENGTH)
            )
              return;
            onUpdate(id, topicDescription);
          }}
          onCloseExpandDialog={() => {
            if (
              description === topicDescription ||
              (topicDescription?.length && topicDescription?.length < GROUP_SN_FIELD_MIN_LENGTH)
            )
              return;
            onUpdate(id, topicDescription);
          }}
          placeholder="Enter topic descriptions"
          error={showError}
          helperText={
            <Typography color={showError ? FORTA_ERROR : FORTA_BLUE.primary} fontSize={14} mt={0.5}>
              {`Minimum ${GROUP_SN_FIELD_MIN_LENGTH} characters`}
            </Typography>
          }
        ></FHTextarea>
      )}
      {!isEditable && (
        <Typography color={FORTA_GRAY4} textAlign="left" mt={0.5}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

function GroupTrainingTopics({ isEditable, validate }: GroupTrainingTopicsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const {
    defaultTopics,
    current: groupParentSN,
    loading: groupSNLoading,
  } = useSelector((state: RootState) => state.groupParentSupervision);

  const [savedTopics, setSavedTopics] = useState<Array<ITrainingTopic>>([]);
  const [selectedTopic, setSelectedTopic] = useState<ITrainingTopic>();

  useEffect(() => {
    if (!groupParentSN) {
      setSavedTopics([]);
      setSelectedTopic(undefined);
      return;
    }
    setSavedTopics(groupParentSN.training_topics ?? []);
  }, [groupParentSN]);

  const handleTopicSelect = (value: any) => {
    setSelectedTopic(value);
  };

  const handleSaveTopic = async () => {
    if (!selectedTopic || !groupParentSN) return;
    const data = {
      description: selectedTopic.description,
      label: selectedTopic.label,
    };
    const response = await dispatch(
      groupParentSupervisionActions.saveTrainingTopic({
        data,
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
      }),
    );
    if (!response.error && response.payload) {
      setSelectedTopic(undefined);
      const bulkInsertData = {
        training_topic_ids: [response.payload.id],
        family_attendance_ids: groupParentSN.family_attendance
          .filter(
            (attendance) =>
              moment(attendance.date_end).isAfter(moment()) || // Don't include families that have left the group
              attendance.duration === groupParentSN.duration, // Don't filter out families that attended full duration
          )
          .map((attendance) => attendance.id),
      };
      if (bulkInsertData.family_attendance_ids.length > 0) {
        dispatch(
          groupParentSupervisionActions.createBulkTopicFamilyMappings({
            data: bulkInsertData,
            getAccessTokenSilently,
            groupSNId: groupParentSN.id,
          }),
        );
      }
    }
  };

  const handleTopicDelete = (topicId: number) => {
    if (!topicId || !groupParentSN) return;
    dispatch(
      groupParentSupervisionActions.deleteTrainingTopic({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
        topicId,
      }),
    );
  };

  const handleTopicUpdate = (topicId: number, description: string) => {
    if (!topicId || !groupParentSN) return;
    const data = {
      description: description ? description : null,
    };
    dispatch(
      groupParentSupervisionActions.updateTrainingTopic({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
        topicId,
        data,
      }),
    );
  };

  const savedLabels = savedTopics.map((item) => item.label);
  const topicOptions = defaultTopics.filter((item) => !savedLabels.includes(item.label));

  return (
    <FBoxContainer sx={{ flexGrow: 0, mt: 3, boxShadow: 'none' }}>
      <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign={'left'} mb={3}>
        Training topics
      </Typography>
      {validate && savedTopics.length < GROUP_SN_MIN_TOPICS && isEditable && (
        <Box sx={{ display: 'flex', mb: 3 }}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
          <Typography
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'20px'}
            ml={0.5}
            mt={-0.1}
            sx={{ cursor: 'pointer' }}
            color={FORTA_ERROR}
          >
            At least one topic must be added
          </Typography>
        </Box>
      )}
      {isEditable && savedTopics?.length < GROUP_SN_MAX_TOPICS && (
        <Grid container>
          <Grid item md={7} sm={12}>
            <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
              Topic
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <FHAutocomplete
                sx={{
                  width: '100%',
                  '& input::placeholder': {
                    color: `${FORTA_GRAY3} !important`,
                  },
                  boxShadow: 'none',
                  borderRadius: '8px 0 0 8px',
                  borderRight: 'none',
                }}
                inputHeight={40}
                data-testid="gspnTopicDropdown"
                disableClearable
                disabled={!groupParentSN || groupSNLoading}
                value={selectedTopic ?? ''}
                error={validate && !selectedTopic && savedTopics.length < 1}
                placeholder="Select topic"
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                options={topicOptions}
                onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                  handleTopicSelect(newValue);
                }}
              />
              <FHButton
                data-testid="addTopicButton"
                startIcon={<PlusIcon color={FORTA_WHITE} />}
                onClick={handleSaveTopic}
                disabled={!selectedTopic || groupSNLoading}
                sx={{
                  color: FORTA_WHITE,
                  borderRadius: '0 8px 8px 0',
                  width: '125px',
                  boxShadow: 'none',
                }}
              >
                Topic
              </FHButton>
            </Box>
          </Grid>
        </Grid>
      )}
      {savedTopics.map((item) => (
        <TrainingTopicItem
          key={item.id}
          id={item.id}
          disabled={groupSNLoading}
          validate={validate}
          label={item.label}
          description={item.description}
          isEditable={isEditable}
          onDelete={handleTopicDelete}
          onUpdate={handleTopicUpdate}
        />
      ))}
    </FBoxContainer>
  );
}

export default GroupTrainingTopics;
